import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { imagesAPI } from '../api/client'
import { Check, ExternalLink, Globe, HardDrive, Upload, X } from 'lucide-react'

const STATUS_FILTERS = ['all', 'parsed', 'approved', 'downloaded', 'published', 'rejected']
const STATUS_LABELS = {
  all: 'Все',
  parsed: 'Распознаны',
  approved: 'Одобрены',
  downloaded: 'Скачаны',
  published: 'Опубликованы',
  rejected: 'Отклонены',
}
const TARGET_FILTERS = [
  { key: 'all', label: 'Все' },
  { key: 'new', label: 'Новые' },
  { key: 'agr', label: 'AGR' },
  { key: 'site', label: 'SITE' },
  { key: 'both', label: 'AGR + SITE' },
  { key: 'site_link', label: 'Со ссылкой сайта' },
  { key: 'published', label: 'Любая публикация' },
]

function getPublishedTargets(image) {
  const targets = new Set(Array.isArray(image?.published_to) ? image.published_to : [])
  if (image?.agr_id) targets.add('agr')
  if (image?.lojs_id) targets.add('lojs')
  return targets
}

function matchesTargetFilter(image, filter) {
  const targets = getPublishedTargets(image)
  const hasAgr = targets.has('agr')
  const hasSite = targets.has('lojs')

  switch (filter) {
    case 'new':
      return !hasAgr && !hasSite
    case 'agr':
      return hasAgr && !hasSite
    case 'site':
      return hasSite && !hasAgr
    case 'both':
      return hasAgr && hasSite
    case 'site_link':
      return Boolean(image?.lojs_public_url)
    case 'published':
      return hasAgr || hasSite
    case 'all':
    default:
      return true
  }
}

function getPublishBadges(image) {
  const badges = []
  const targets = getPublishedTargets(image)

  if (targets.has('agr')) {
    badges.push({ key: 'agr', label: 'AGR', className: 'bg-blue-500/80' })
  }
  if (targets.has('lojs')) {
    badges.push({ key: 'lojs', label: 'SITE', className: 'bg-orange-500/80' })
  }

  return badges
}

function getPublishStateSummary(image) {
  const targets = getPublishedTargets(image)
  const hasAgr = targets.has('agr')
  const hasSite = targets.has('lojs')

  if (hasAgr && hasSite) return { label: 'AGR + сайт', className: 'text-purple-300' }
  if (hasAgr) return { label: 'Только AGR', className: 'text-blue-300' }
  if (hasSite) return { label: 'Только сайт', className: 'text-orange-300' }
  return { label: 'Новая', className: 'text-green-300' }
}

function getSiteSeoState(image) {
  const status = String(image?.lojs_seo_status || '').trim().toLowerCase()
  const hasSitePage = Boolean(image?.lojs_id || image?.lojs_public_url)

  if (!hasSitePage) return null
  if (status === 'done') return { label: 'SEO готово', className: 'text-emerald-300', badgeClassName: 'bg-emerald-500/80' }
  if (status === 'failed' || status === 'error') return { label: 'SEO ошибка', className: 'text-red-300', badgeClassName: 'bg-red-500/80' }
  if (status === 'pending' || status === 'leased' || status === 'queued' || status === 'processing') {
    return { label: 'SEO в работе', className: 'text-amber-300', badgeClassName: 'bg-amber-500/80' }
  }
  return { label: 'SEO неизвестно', className: 'text-slate-300', badgeClassName: 'bg-slate-500/80' }
}

function TargetStat({ icon: Icon, label, value, className }) {
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 mb-2 opacity-60">
        <Icon size={16} />
        <span className="text-sm">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${className}`}>{value}</div>
    </div>
  )
}

function ImageCard({ image, onReject, onApprove, onPublish }) {
  const [showModal, setShowModal] = useState(false)
  const badges = getPublishBadges(image)
  const publishState = getPublishStateSummary(image)
  const siteSeoState = getSiteSeoState(image)
  const canApprove = !['approved', 'downloaded', 'published', 'rejected'].includes(image.status)
  const canPublish = image.status === 'downloaded' || image.status === 'published'
  const targets = getPublishedTargets(image)
  const canPublishAgr = canPublish && !targets.has('agr')
  const canPublishSite = canPublish && !targets.has('lojs')

  return (
    <>
      <div className="image-card group cursor-pointer relative" onClick={() => setShowModal(true)}>
        <img
          src={image.thumbnail_url || image.image_url}
          alt={image.title || ''}
          loading="lazy"
        />
        {image.lojs_public_url ? (
          <a
            href={`https://legkie-otkrytki.ru${image.lojs_public_url}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="absolute top-2 left-2 p-2 rounded-full bg-cyan-500/85 text-white hover:bg-cyan-500 z-10"
            title="Открыть на сайте"
          >
            <Globe size={14} />
          </a>
        ) : null}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <span className={`badge badge-${image.status}`}>{image.status}</span>
        </div>
        {badges.length > 0 && (
          <div className="absolute bottom-2 left-2 flex flex-wrap gap-1 max-w-[85%]">
            {badges.map(badge => (
              <span
                key={badge.key}
                className={`px-2 py-1 rounded text-xs font-bold ${badge.className}`}
              >
                {badge.label}
              </span>
            ))}
          </div>
        )}
        {siteSeoState ? (
          <div className="absolute bottom-2 right-2">
            <span className={`px-2 py-1 rounded text-xs font-bold ${siteSeoState.badgeClassName}`}>
              {siteSeoState.label}
            </span>
          </div>
        ) : null}
      </div>

      {showModal && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="glass p-6 max-w-3xl w-full max-h-[90vh] overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <h2 className="text-xl font-bold">{image.title || 'Без названия'}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className={`badge badge-${image.status}`}>{STATUS_LABELS[image.status] || image.status}</span>
                  {badges.map(badge => (
                    <span key={badge.key} className={`px-2 py-1 rounded text-xs font-bold ${badge.className}`}>
                      {badge.label}
                    </span>
                  ))}
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="btn-ghost p-2">
                <X size={20} />
              </button>
            </div>

            <img
              src={image.image_url || image.thumbnail_url}
              alt=""
              className="w-full rounded-lg mb-4"
            />

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm opacity-50">Источник</p>
                <p>{image.source_name || 'Неизвестно'}</p>
              </div>
              <div>
                <p className="text-sm opacity-50">Автор</p>
                <p>{image.author_name || 'Неизвестно'}</p>
              </div>
              <div>
                <p className="text-sm opacity-50">Внешний ID</p>
                <p className="font-mono text-sm">{image.external_id}</p>
              </div>
              <div>
                <p className="text-sm opacity-50">Статус публикации</p>
                <p className={publishState.className}>{publishState.label}</p>
              </div>
              <div>
                <p className="text-sm opacity-50">SEO на сайте</p>
                <p className={siteSeoState?.className || 'opacity-50'}>{siteSeoState?.label || 'ещё нет страницы'}</p>
              </div>
            </div>

            {image.page_url && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                <a
                  href={image.page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost flex items-center justify-center gap-2 w-full"
                >
                  <ExternalLink size={18} />
                  Открыть оригинал
                </a>
                {image.lojs_public_url ? (
                  <a
                    href={`https://legkie-otkrytki.ru${image.lojs_public_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost flex items-center justify-center gap-2 w-full"
                  >
                    <Globe size={18} />
                    Открыть на сайте
                  </a>
                ) : null}
              </div>
            )}
            {!image.page_url && image.lojs_public_url ? (
              <a
                href={`https://legkie-otkrytki.ru${image.lojs_public_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost flex items-center justify-center gap-2 w-full mb-4"
              >
                <Globe size={18} />
                Открыть на сайте
              </a>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {canApprove && (
                <button
                  onClick={() => {
                    onApprove(image.id)
                    setShowModal(false)
                  }}
                  className="btn-success flex items-center justify-center gap-2"
                >
                  <Check size={18} />
                  Одобрить
                </button>
              )}

              {canPublishAgr && (
                <button
                  onClick={() => {
                    onPublish(image.id, 'agr')
                    setShowModal(false)
                  }}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <HardDrive size={18} />
                  Опубликовать в AGR
                </button>
              )}

              {canPublishSite && (
                <button
                  onClick={() => {
                    onPublish(image.id, 'lojs')
                    setShowModal(false)
                  }}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Globe size={18} />
                  Опубликовать на сайт
                </button>
              )}

              {canPublishAgr && canPublishSite && (
                <button
                  onClick={() => {
                    onPublish(image.id, 'both')
                    setShowModal(false)
                  }}
                  className="btn-success flex items-center justify-center gap-2"
                >
                  <Upload size={18} />
                  Опубликовать везде
                </button>
              )}

              {['parsed', 'downloaded', 'approved'].includes(image.status) && (
                <button
                  onClick={() => {
                    onReject(image.id)
                    setShowModal(false)
                  }}
                  className="btn-danger flex-1"
                >
                  Отклонить
                </button>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between gap-2 text-xs opacity-70">
              <span className={publishState.className}>{publishState.label}</span>
              <span>{siteSeoState?.label || (badges.length ? badges.map((badge) => badge.label).join(' + ') : 'ещё не опубликовано')}</span>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default function Images() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('all')
  const [targetFilter, setTargetFilter] = useState('all')

  const { data: images, isLoading } = useQuery(
    ['images', statusFilter],
    () => imagesAPI.getAll({ status: statusFilter !== 'all' ? statusFilter : undefined, limit: 100 })
  )

  const { data: stats } = useQuery('imageStats', imagesAPI.getStats)
  const { data: tierCounts } = useQuery('imageTierCountsForImagesPage', () => imagesAPI.getTierCounts())

  const rejectMutation = useMutation(imagesAPI.reject, {
    onSuccess: () => {
      queryClient.invalidateQueries('images')
      queryClient.invalidateQueries('imageStats')
      queryClient.invalidateQueries('imageTierCountsForImagesPage')
    }
  })

  const approveMutation = useMutation(imagesAPI.approve, {
    onSuccess: () => {
      queryClient.invalidateQueries('images')
      queryClient.invalidateQueries('imageStats')
      queryClient.invalidateQueries('imageTierCountsForImagesPage')
    }
  })

  const publishMutation = useMutation(
    ({ id, target }) => imagesAPI.publish(id, target),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('images')
        queryClient.invalidateQueries('imageStats')
        queryClient.invalidateQueries('imageTierCountsForImagesPage')
      }
    }
  )

  const visibleImages = useMemo(
    () => (images || []).filter(image => matchesTargetFilter(image, targetFilter)),
    [images, targetFilter]
  )

  const visibleCounts = useMemo(() => {
    const base = images || []
    return TARGET_FILTERS.reduce((acc, item) => {
      acc[item.key] = base.filter(image => matchesTargetFilter(image, item.key)).length
      return acc
    }, {})
  }, [images])

  const targetStats = tierCounts?.targets || {}

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="text-2xl font-bold">Изображения</h1>
        {stats && (
          <div className="flex gap-4 text-sm flex-wrap">
            <span>Всего: {stats.total}</span>
            <span className="text-yellow-400">Распознано: {stats.parsed}</span>
            <span className="text-blue-400">Скачано: {stats.downloaded}</span>
            <span className="text-green-400">Опубликовано: {stats.published}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <TargetStat icon={HardDrive} label="Только AGR" value={targetStats.agr_only || 0} className="text-blue-300" />
        <TargetStat icon={Globe} label="Только сайт" value={targetStats.site_only || 0} className="text-orange-300" />
        <TargetStat icon={Upload} label="AGR + SITE" value={targetStats.both || 0} className="text-purple-300" />
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {STATUS_FILTERS.map(status => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`btn-ghost ${statusFilter === status ? 'bg-primary/20 text-primary' : ''}`}
          >
            {STATUS_LABELS[status] || status}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {TARGET_FILTERS.map(item => (
          <button
            key={item.key}
            onClick={() => setTargetFilter(item.key)}
            className={`btn-ghost ${targetFilter === item.key ? 'bg-primary/20 text-primary' : ''}`}
          >
            {item.label}
            {visibleCounts[item.key] > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-white/10 rounded text-xs">
                {visibleCounts[item.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12 opacity-50">Загрузка...</div>
      ) : visibleImages.length === 0 ? (
        <div className="text-center py-12 opacity-50">Изображения не найдены</div>
      ) : (
        <div className="image-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {visibleImages.map(image => (
            <ImageCard
              key={image.id}
              image={image}
              onReject={(id) => rejectMutation.mutate(id)}
              onApprove={(id) => approveMutation.mutate(id)}
              onPublish={(id, target) => publishMutation.mutate({ id, target })}
            />
          ))}
        </div>
      )}
    </div>
  )
}
